/**
 * UpdatePrompt — fires when the service worker installs a new version.
 * Lets the user trigger an instant reload to pick up the new code.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { applyServiceWorkerUpdate } from "@/pwa";
import { Sparkles, X } from "lucide-react";

export function UpdatePrompt() {
  const { t } = useI18n();
  const [show, setShow] = useState(false);

  useImperativeLoad((() => () => {
    const handler = () => setShow(true);
    window.addEventListener("pwa:updateavailable", handler);
  })(), []);

  if (!show) return null;
  return (
    <div
      className="fixed left-4 right-4 top-4 z-[60] mx-auto max-w-md rounded-2xl border border-[#9ACD32]/30 bg-[#0A0C1F]/95 p-4 shadow-2xl backdrop-blur-lg sm:top-6"
      data-testid="pwa-update-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#9ACD32]">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold text-white">
            {t("pwa.update_title")}
          </h3>
          <p className="mt-1 text-xs leading-snug text-slate-400">
            {t("pwa.update_desc")}
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              onClick={applyServiceWorkerUpdate}
              className="h-9 rounded-full bg-[#9ACD32] px-4 text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="pwa-update-reload-btn"
            >
              {t("pwa.update_reload")}
            </Button>
            <Button
              onClick={() => setShow(false)}
              variant="ghost"
              className="h-9 rounded-full text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              data-testid="pwa-update-later-btn"
            >
              {t("pwa.update_later")}
            </Button>
          </div>
        </div>
        <button
          onClick={() => setShow(false)}
          className="ml-1 shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
          aria-label={t("common.close")}
          data-testid="pwa-update-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
